'use client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from '@fshr/context/i18n-context';
import { UserPanelTabId } from '@fshr/shared/types';
import { rumAddToCartPressed } from '@fshr/shared/rum';

import useAddToCartParams from './use-add-to-cart-params';
import getBookingItineraryPath from './get-booking-itinerary-path';
import {
	BOOKED,
	BOOKING_FLOW_STEP,
	DEFAULT_ITINERARY_DISCLAIMER,
	NO_BOOKING,
	PENDING_BOOKING,
	PENDING_MOD_BOOKING,
} from './constants';
import type { BookingFlowStep } from './constants';
import type {
	Booking,
	DateRange,
	EventProduct,
	NormalizedFieldSet,
	Price,
	ProductBookingWidgetProps,
} from './types';
const noop = () => undefined;

type AbortableRequest<T = unknown> = { request: Promise<T>; abort: () => void };

/**
 * The full booking-flow "brain" for the widget - ported from `AddToCart.tsx`, `AddToCartMobileWrapper`,
 * `AddToCartFormModal.tsx` and `ProductPrice.tsx`. The one structural change from the legacy widget is
 * that selecting a price no longer opens a modal: `selectedPriceId` + `flowStep` track which price's
 * form/confirmation/success section should render inline instead.
 */
export default function useProductBookingWidget({
	locale,
	productId,
	enhancementAssetType,
	enhancementMerchA,
	getEveryPageAnalyticsVariables,
	productAvailabilityStore,
	bookingsStore,
	cartStore,
	userStore,
	globalStore,
	property,
	onAvailabilityChecked,
	onSuccessfullyAddedProduct = noop,
}: ProductBookingWidgetProps & { locale: string }) {
	const t = useTranslations();

	const hotelCode = property.bookingConfig.owsCode;

	const itineraryDisclaimer =
		userStore?.globalSettings?.itineraryDisclaimer ?? DEFAULT_ITINERARY_DISCLAIMER;
	const params = useAddToCartParams({
		bookingsStore,
		cartStore,
		productAvailabilityStore,
		productId,
	});
	const {
		product,
		isLoading,
		dateRangeStart,
		dateRangeEnd,
		propertyContent,
		hasActiveBookingDateRange,
		activeBooking,
		bookingsForProperty,
	} = params;

	// ---- booking status -----------------------------------------------------------------------
	const bookingStatus = activeBooking?.bookingStatus || NO_BOOKING;
	const isBookingPending = bookingStatus === PENDING_BOOKING;
	const isPendingModBooking = bookingStatus === PENDING_MOD_BOOKING;
	const isBooked = bookingStatus === BOOKED || isPendingModBooking;
	const buttonLabel = isBooked ? t('Add to Itinerary') : t('Add To Cart');
	const hasMultipleBookings = bookingsForProperty.length > 1;
	const fsSecurity = bookingsStore.getFSSecurityMX?.();
	const isPartiallyAuthenticated = Boolean(fsSecurity?.authorized && !fsSecurity?.persisted);

	const hotelProducts = activeBooking?.hotelProducts ?? [];
	const isMultiRoomBooking = hotelProducts.length > 1;
	const shouldShowInteractiveElements = Boolean(
		!isMultiRoomBooking && product && !product.hidden && product.category.cartEnabled
	);
	const shouldShowFindReservationComponent =
		!isLoading && !hasActiveBookingDateRange && !userStore.surname;

	// ---- prices (+ synthesized email fieldset when booked contact info is missing) ------------
	// The store's `Price`/`PriceFieldSet` types are a discriminated union that doesn't line up cleanly
	// with `NormalizedFieldSet` (used throughout this widget for easier destructuring), so this is built
	// up loosely and cast back to `Price[]` at the boundary.
	const prices = useMemo((): Price[] => {
		const pricesAreForSameDay = Boolean(
			productAvailabilityStore.dateRange.start &&
				productAvailabilityStore.dateRange.end &&
				new Date(productAvailabilityStore.dateRange.start).toDateString() ===
					new Date(productAvailabilityStore.dateRange.end).toDateString()
		);

		const basePrices = (product?.prices || []).filter(
			({ fieldSets = [] }) => fieldSets.length > 0
		) as Price[];

		const stripped = pricesAreForSameDay
			? basePrices.map(price => {
					const { availabilitySummary: _availabilitySummary, ...rest } = price;
					return rest as Price;
				})
			: basePrices;

		return stripped.map(price => {
			let addEmailFieldset = false;
			let nextIndex = 0;

			if (isBooked && isPartiallyAuthenticated) {
				const emailAddress = activeBooking?.contactDetails?.email;
				const phoneNumber = activeBooking?.contactDetails?.phones?.[0]?.number;
				if (!emailAddress && !phoneNumber) {
					addEmailFieldset = true;
					(price.fieldSets as unknown as NormalizedFieldSet[])?.forEach(fieldSet => {
						fieldSet.fields.forEach(field => {
							if (field.name?.startsWith('details.fields')) nextIndex++;
						});
					});
				}
			}

			if (!addEmailFieldset) return price;

			const emailFieldset: NormalizedFieldSet = {
				label: t('Please share your preferred contact email'),
				secondaryLabel: null,
				hidden: false,
				fields: [
					{
						name: `details.fields[${nextIndex}].Requested_By`,
						required: true,
						type: 'EMAIL',
						additionalNames: [],
						label: t('Email'),
						secondaryLabel: null,
					},
				],
			};

			return {
				...price,
				fieldSets: (price.fieldSets as unknown as NormalizedFieldSet[]).flatMap(fieldSet => {
					if (fieldSet.fields?.[0]?.name?.endsWith('.Guest_Details')) {
						return [emailFieldset, fieldSet];
					}
					return fieldSet;
				}),
			} as unknown as Price;
		});
	}, [
		product,
		isBooked,
		isPartiallyAuthenticated,
		activeBooking,
		t,
		productAvailabilityStore.dateRange,
	]);

	const itineraryPath = getBookingItineraryPath({
		locale,
		activeBooking,
		bookings: bookingsForProperty,
		isSignedIn: userStore.isSignedIn,
	});

	// ---- local state ----------------------------------------------------------------------------
	const [errorState, setError] = useState<unknown>(null);
	const [selectedPriceId, setSelectedPriceId] = useState<string | null>(null);
	const [flowStep, setFlowStep] = useState<BookingFlowStep>(BOOKING_FLOW_STEP.FORM);
	const [addedEventProduct, setAddedEventProduct] = useState<EventProduct | null>(null);
	/** The cart-flow item just added - drives AddedToCartPopup. */
	const [addedToCartItem, setAddedToCartItem] = useState<EventProduct | null>(null);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isRemoveStayConfirmOpen, setIsRemoveStayConfirmOpen] = useState(false);

	const abortFns = useRef<Array<() => void>>([]);
	const modalAbortFn = useRef<() => void>(noop);
	const pendingFormData = useRef<unknown>(null);

	const selectedPrice = useMemo(
		() => prices.find(price => price.id === selectedPriceId) ?? null,
		[prices, selectedPriceId]
	);

	const displayError = errorState || cartStore.error || bookingsStore.error;

	// ---- effects ----------------------------------------------------------------------------------

	// Fires once per product/date-range combo, mirroring `analyticEvents.enhancementAvailCheck`.
	useEffect(() => {
		const enhancementName = product?.name;
		const enhancementType = product?.taxonomy?.type;
		const enhancementCategory = product?.taxonomy?.category;
		const enhancementSubcategory = product?.taxonomy?.subCategory;
		const enhancementSku = product?.sku;
		const hasProductFullFields =
			enhancementName && enhancementType && enhancementCategory && enhancementSubcategory;
		const hasDateRange = dateRangeStart && dateRangeEnd;

		if (!isLoading && hasDateRange && hasProductFullFields && !hasActiveBookingDateRange) {
			onAvailabilityChecked?.({
				startDate: dateRangeStart,
				endDate: dateRangeEnd,
				propertyContent,
				enhancementName,
				enhancementType,
				enhancementCategory,
				enhancementSubcategory,
				enhancementAssetType,
				enhancementSku,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLoading, dateRangeStart?.getTime(), dateRangeEnd?.getTime(), product]);

	// A session timing out mid-flow should drop the inline form back to the price list and surface the cart panel.
	useEffect(() => {
		if (
			cartStore.error &&
			(cartStore.error as { name?: string }).name === 'SessionHasExpiredError'
		) {
			setSelectedPriceId(null);
			globalStore?.setUserPanelOpenTab(UserPanelTabId.CART);
		}
	}, [cartStore, cartStore.error, globalStore]);

	useEffect(() => {
		return () => {
			abortFns.current.forEach(abort => abort());
			modalAbortFn.current();
		};
	}, []);

	// ---- actions ------------------------------------------------------------------------------------

	const selectActiveBooking = useCallback(
		(reservationId: string): AbortableRequest<void> => {
			abortFns.current.forEach(abort => abort());
			const { request, abort } = bookingsStore.fetchFullBooking({ reservationId });
			abortFns.current = [...abortFns.current, abort];

			return {
				request: request
					.then((booking: Booking) => bookingsStore.setActiveBookingId(booking.bookingId))
					.catch(noop),
				abort,
			};
		},
		[bookingsStore]
	);

	/** Ported from the legacy `FindReservationModal` -> `bookingsStore.findReservation`: looks up a
	 * reservation by resort/last-name/confirmation-number and, on success, sets it as the booking for
	 * this property (mobx reactivity does the rest, same as the legacy modal). */
	const findReservation = useCallback(
		async (values: { resortCode: string; lastName: string; confirmationNumber: string }) => {
			await bookingsStore.findReservation(
				values.confirmationNumber,
				values.lastName,
				values.resortCode
			);
		},
		[bookingsStore]
	);

	const applyDateRange = useCallback(
		(range: DateRange) => {
			productAvailabilityStore.setDateRange(range);
		},
		[productAvailabilityStore]
	);

	/** Routes to the "already on an itinerary" flow (bookingsStore) or the pre-booking cart flow
	 * (cartStore), matching `addEventToActiveBooking` in the legacy `AddToCart.tsx`. */
	const addEventToActiveBookingOrCart = useCallback(
		({
			formData,
			forceAddToCart = false,
		}: {
			formData: unknown;
			forceAddToCart?: boolean;
		}): AbortableRequest => {
			setError(null);

			if (isBooked) {
				return bookingsStore.addEventToActiveBooking({
					formData,
					propertyContent,
					enhancementAssetType,
				});
			}

			const { request, abort } = cartStore.addToCart({
				formData,
				propertyContent,
				hotelCode,
				forceAddToCart,
				enhancementAssetType,
				// The widget confirms with AddedToCartPopup instead; its View Cart CTA opens the cart.
				openCart: false,
				analyticsContext: {
					...(getEveryPageAnalyticsVariables
						? { everyPageVariables: getEveryPageAnalyticsVariables() }
						: {}),
					pageDetail: 'select experience',
					startDate: dateRangeStart,
					endDate: dateRangeEnd,
					enhancementMerchA,
				},
			});

			return {
				request: request.then(result => {
					onSuccessfullyAddedProduct();
					return result;
				}),
				abort,
			};
		},
		[
			isBooked,
			bookingsStore,
			cartStore,
			propertyContent,
			enhancementAssetType,
			enhancementMerchA,
			getEveryPageAnalyticsVariables,
			hotelCode,
			dateRangeStart,
			dateRangeEnd,
			onSuccessfullyAddedProduct,
		]
	);

	/** Ported from `AddToCartFormModal._addEventToActiveBooking`. Submits the guest/quantity/fields form
	 * for the currently selected price; on the "already booked" flow this moves the flow into the
	 * confirmation (price-breakdown) step instead of finishing outright. */
	const submitBookingForm = useCallback(
		(formData: unknown, forceAddToCart = false) => {
			setIsSubmitting(true);
			const { request, abort } = addEventToActiveBookingOrCart({ formData, forceAddToCart });
			modalAbortFn.current = abort;

			return request
				.then(result => {
					const booking = result as Booking;
					setIsSubmitting(false);
					if (isBooked) {
						const code = (formData as { code?: string })?.code;
						setAddedEventProduct(booking?.eventProducts?.find(ep => ep.code === code) ?? null);
						setFlowStep(BOOKING_FLOW_STEP.CONFIRMATION);
					} else {
						setSelectedPriceId(null);
						productAvailabilityStore.setDateRange({ start: undefined, end: undefined });
						const { productId } = (result ?? {}) as { productId?: number | string };
						const added = (cartStore.booking?.eventProducts as EventProduct[] | undefined)?.find(
							ep => productId !== undefined && String(ep.productId) === String(productId)
						);
						// No match means the refreshed booking didn't carry the item - fall back to the cart.
						if (added) setAddedToCartItem(added);
						else globalStore?.setUserPanelOpenTab(UserPanelTabId.CART);
					}
				})
				.catch(err => {
					setIsSubmitting(false);
					if (
						err === 'HAS_BOOKING_FOR_DIFFERENT_PROPERTY' ||
						err === 'HAS_JOURNEY_BOOKING_IN_CART'
					) {
						pendingFormData.current = formData;
						setIsRemoveStayConfirmOpen(true);
					}
					// Any other error is surfaced reactively via cartStore.error / bookingsStore.error.
				});
		},
		[addEventToActiveBookingOrCart, isBooked, cartStore, globalStore, productAvailabilityStore]
	);

	const confirmRemoveStayAndRetry = useCallback(() => {
		rumAddToCartPressed({
			cartType: 'product',
			hotelCode,
			forceAddToCart: true,
			productName: (pendingFormData.current as { name?: string } | null)?.name,
		});
		setIsRemoveStayConfirmOpen(false);
		submitBookingForm(pendingFormData.current, true);
	}, [submitBookingForm, hotelCode]);

	/** Ported from `AddToCartFormModal`'s inline `confirmEventsOnActiveBooking` wrapper. */
	const confirmOnItinerary = useCallback(() => {
		setIsSubmitting(true);
		setError(null);
		const { request } = bookingsStore.confirmEventsOnActiveBooking({
			enhancementAssetType,
			propertyContent,
			analyticsContext: {
				...(getEveryPageAnalyticsVariables
					? { everyPageVariables: getEveryPageAnalyticsVariables() }
					: {}),
				pageDetail: 'select experience',
				startDate: dateRangeStart,
				endDate: dateRangeEnd,
				enhancementMerchA,
				cartState: 'empty',
			},
		});

		return request
			.then(() => {
				onSuccessfullyAddedProduct();
				setFlowStep(BOOKING_FLOW_STEP.SUCCESS);
				setIsSubmitting(false);
			})
			.catch(inputError => {
				setIsSubmitting(false);
				if (!(inputError as { code?: string })?.code?.includes('ERR_CANCELED')) {
					setError(inputError);
				}
			});
	}, [
		bookingsStore,
		enhancementAssetType,
		propertyContent,
		dateRangeStart,
		dateRangeEnd,
		enhancementMerchA,
		getEveryPageAnalyticsVariables,
		onSuccessfullyAddedProduct,
	]);

	/** Ported from `AddToCartFormModal`'s inline `goBack`: clears the on-request events staged for the
	 * confirmation step and drops back to the form. */
	const goBackToForm = useCallback(() => {
		setIsSubmitting(true);
		const { request } = bookingsStore.clearEventsFromActiveBooking();
		return request.then(() => {
			setFlowStep(BOOKING_FLOW_STEP.FORM);
			setAddedEventProduct(null);
			setIsSubmitting(false);
		});
	}, [bookingsStore]);

	const selectPrice = useCallback(
		(priceId: string) => {
			bookingsStore.clearEventsFromActiveBooking();
			setSelectedPriceId(priceId);
			setFlowStep(BOOKING_FLOW_STEP.FORM);
			setAddedEventProduct(null);
			setError(null);
		},
		[bookingsStore]
	);

	const closeForm = useCallback(() => {
		modalAbortFn.current();
		setSelectedPriceId(null);
		setFlowStep(BOOKING_FLOW_STEP.FORM);
		setAddedEventProduct(null);
		setError(null);
		cartStore.updateError(null);
	}, [cartStore]);

	const closeAddedToCartPopup = useCallback(() => setAddedToCartItem(null), []);

	const viewCartFromPopup = useCallback(() => {
		setAddedToCartItem(null);
		globalStore?.setUserPanelOpenTab(UserPanelTabId.CART);
	}, [globalStore]);

	const viewInItinerary = useCallback(() => {
		closeForm();
		globalStore?.setUserPanelOpenTab(UserPanelTabId.ITINERARY);
	}, [closeForm, globalStore]);

	return {
		t,
		locale,
		...params,
		bookingStatus,
		isBookingPending,
		isPendingModBooking,
		isBooked,
		buttonLabel,
		hasMultipleBookings,
		shouldShowInteractiveElements,
		shouldShowFindReservationComponent,
		prices,
		itineraryPath,
		itineraryDisclaimer,
		hotelCode,
		error: displayError,

		selectedPriceId,
		selectedPrice,
		flowStep,
		addedEventProduct,
		addedToCartItem,
		isSubmitting,
		isRemoveStayConfirmOpen,

		selectActiveBooking,
		findReservation,
		applyDateRange,
		selectPrice,
		closeForm,
		submitBookingForm,
		confirmOnItinerary,
		goBackToForm,
		confirmRemoveStayAndRetry,
		cancelRemoveStayConfirm: () => setIsRemoveStayConfirmOpen(false),
		viewInItinerary,
		closeAddedToCartPopup,
		viewCartFromPopup,
	};
}
